import {
  AddIcon,
  CloseIcon,
  EditIcon,
  RepeatIcon,
  SearchIcon,
} from '@chakra-ui/icons';
import {
  Box,
  Button,
  Center,
  Divider,
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  LinkBox,
  Spinner,
  Text,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import { Link as ReactRouterLink } from 'react-router-dom';
import CreateLearningPlanMd from 'components/learningPlan/modals/CreateLearningPlanMd';
import DeleteLearningPlanMd from 'components/learningPlan/modals/DeleteLearningPlanMd';
import UpdateLearningPlanMd from 'components/learningPlan/modals/UpdateLearningPlanMd';
import useToastNotif from 'hooks/useToastNotif';
import debounce from 'lodash.debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { useDispatch, useSelector } from 'react-redux';
import { getLearningPlansAction } from 'store/modules/learningPlan/learningPlan-action';
import { learningPlanActions } from 'store/modules/learningPlan/learningPlan-slice';
import learningPlanColumns from 'utils/columns/learningPlanColumns';
import { ReducerStatus } from 'utils/constants';
import { TOAST_ERROR } from 'utils/constants/toast';
import { FaEye } from 'react-icons/fa';

export const ActionColumn = () => {
  const dispatch = useDispatch();

  const handleEditButton = (id) => {
    dispatch(learningPlanActions.setUpdateLearningPlan({ modal: true, id }));
  };

  const handleDeleteButton = (id) => {
    dispatch(learningPlanActions.setDeleteLearningPlan({ modal: true, id }));
  };

  return {
    name: 'Action',
    sortable: false,
    cell: (row) => (
      <Flex gap={2}>
        <Tooltip label="View" fontSize="md">
          <LinkBox as={ReactRouterLink} to={`/admin/learning-plans/${row.id}`}>
            <Button colorScheme="green" size="xs">
              <FaEye />
            </Button>
          </LinkBox>
        </Tooltip>

        <Tooltip label="Edit" fontSize="md">
          <Button
            colorScheme="blue"
            size="xs"
            onClick={() => handleEditButton(row.id)}
          >
            <EditIcon />
          </Button>
        </Tooltip>

        <Tooltip label="Remove" fontSize="md">
          <Button
            colorScheme="gray"
            size="xs"
            onClick={() => handleDeleteButton(row.id)}
          >
            <CloseIcon />
          </Button>
        </Tooltip>
      </Flex>
    ),
  };
};

function LearningPlanListPage() {
  document.title = 'Skill Quest | Admin - Learning Plans';

  const dispatch = useDispatch();

  const { generateToastNotif } = useToastNotif();

  const learningPlans = useSelector(
    (state) => state.learningPlan.learningPlans
  );

  const learningPlansMetaData = useSelector(
    (state) => state.learningPlan.learningPlansMetaData
  );

  const getLearningPlans = useSelector(
    (state) => state.learningPlan.getLearningPlans
  );

  const [perPage, setPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('asc');
  const [sortBy, setSortBy] = useState('');
  const [searchText, setSearchText] = useState('');

  const fetchData = useCallback(
    ({
      selectedPage,
      selectedPageSize,
      selectedOrderBy,
      selectedSortBy,
      selectedQuery,
    }) => {
      dispatch(
        getLearningPlansAction({
          currentPage: selectedPage || 1,
          pageSize: selectedPageSize || 10,
          orderBy: selectedOrderBy,
          sortBy: selectedSortBy,
          query: selectedQuery,
        })
      );
    },
    [dispatch]
  );

  const handlePerRowsChange = (newPerPage, page) => {
    if (newPerPage === perPage) return;
    setPerPage(newPerPage);
    fetchData({
      selectedPage: page,
      selectedPageSize: newPerPage,
      selectedOrderBy: orderBy,
      selectedSortBy: sortBy,
      selectedQuery: searchText,
    });
  };

  const handleSort = (column, sortDirection) => {
    if (!column?.sortField && sortDirection === orderBy) return;
    setOrderBy(sortDirection);
    setSortBy(column.sortField);
    fetchData({
      selectedPage: 1,
      selectedPageSize: perPage,
      selectedOrderBy: sortDirection,
      selectedSortBy: column.sortField,
      selectedQuery: searchText,
    });
  };

  const handlePageChange = (page) => {
    fetchData({
      selectedPage: page,
      selectedPageSize: perPage,
      selectedOrderBy: orderBy,
      selectedSortBy: sortBy,
      selectedQuery: searchText,
    });
  };

  const debouncedFetchData = useMemo(
    () =>
      debounce((query) => {
        fetchData({
          selectedPage: 1,
          selectedPageSize: perPage,
          selectedQuery: query,
        });
      }, 350),
    [fetchData, perPage]
  );

  // Update search text and trigger debounced fetch
  const handleSearchTextChange = (e) => {
    setSearchText(e.target.value);
    debouncedFetchData(e.target.value);
  };

  const handleCreateLearningPlan = () => {
    dispatch(learningPlanActions.setCreateLearningPlan({ modal: true }));
  };

  useEffect(() => {
    if (getLearningPlans.status === ReducerStatus.FAILED) {
      generateToastNotif(
        TOAST_ERROR,
        getLearningPlans.error?.data?.status || '500',
        getLearningPlans.error?.data?.title ||
          'Something went wrong with the request',
        true,
        false
      );
    }
  }, [getLearningPlans, generateToastNotif]);

  // Load data on component mount
  useEffect(() => {
    fetchData({});
  }, [fetchData]);

  return (
    <Box ml={{ base: 0, md: 20 }} minH="100vh" p="10">
      <CreateLearningPlanMd />
      <UpdateLearningPlanMd />
      <DeleteLearningPlanMd />
      <Flex my={4} align="center">
        <Text flex={1} fontSize="2xl">
          Learning Plans
        </Text>

        <Button
          colorScheme="red"
          leftIcon={<AddIcon />}
          onClick={handleCreateLearningPlan}
        >
          Create
        </Button>
      </Flex>

      <Box w="100%" p={4} color="gray.500" borderWidth="1px" borderRadius="lg">
        <Flex m={2} justify={{ base: 'center', md: 'end' }}>
          <IconButton
            aria-label="Reload table"
            variant="link"
            onClick={fetchData}
            icon={<RepeatIcon />}
          />
          <InputGroup width={{ base: '100%', md: '350px' }}>
            <InputRightElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputRightElement>
            <Input
              name="query"
              placeholder="Search"
              value={searchText}
              onChange={handleSearchTextChange}
            />
          </InputGroup>
        </Flex>

        <Divider mt={4} />

        {/* DataTable component for displaying records */}
        <DataTable
          theme={useColorModeValue('default', 'dark')}
          progressPending={getLearningPlans.status === ReducerStatus.LOADING}
          columns={[...learningPlanColumns, ActionColumn()]}
          data={learningPlans}
          paginationTotalRows={learningPlansMetaData?.totalItems}
          onChangeRowsPerPage={handlePerRowsChange}
          onSort={handleSort}
          onChangePage={handlePageChange}
          pagination
          paginationServer
          sortServer
          progressComponent={
            <Center my={10}>
              <Spinner
                thickness="4px"
                speed="0.65s"
                emptyColor="gray.200"
                color="blue.500"
                size="xl"
              />
            </Center>
          }
        />
      </Box>
    </Box>
  );
}

export default LearningPlanListPage;
